import PropTypes from "prop-types";
import { lazy, Suspense, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// @mui
import { styled, alpha } from "@mui/material/styles";
import {
  Box,
  Drawer,
  Typography,
  Avatar,
  Stack,
  Button,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";

// hooks
import useResponsive from "../../../hooks/useResponsive";

// context & config
import { useProfile } from "../../../Context/ProfileContext";
import { PROFILE_URL } from "../../../Context/config";
import { useCardDialog } from "../../../Context/CardDialogContext";

// components
import CardPurchaseDialog from "../../../components/CardPurchaseDialog";

const NavSection = lazy(() => import("../../../components/nav-section"));
const Logo = lazy(() => import("../../../components/Logo"));

// ----------------------------------------------------------------------

const NAV_WIDTH = 250;

const StyledAccount = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1.8, 2.2),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: alpha(theme.palette.grey[500], 0.04),
  border: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
  transition: theme.transitions.create(["all"], {
    duration: theme.transitions.duration.shorter,
  }),
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.primary.main,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 12px 0 ${alpha(theme.palette.common.black, 0.08)}`,
  },
}));

const VerifiedSeal = styled("div")(({ theme }) => ({
  position: "absolute",
  bottom: -2,
  right: -2,
  width: 18,
  height: 18,
  backgroundColor: "#ffffff",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  padding: "3px",
  zIndex: 2,
  border: `1px solid ${alpha(theme.palette.grey[500], 0.1)}`,
}));

// ----------------------------------------------------------------------

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

export default function Nav({ openNav, onCloseNav }) {
  const { profile } = useProfile();
  const navigate = useNavigate();
  const { open: showCardDialog, closeDialog: closeCardDialog } = useCardDialog();

  const isDesktop = useResponsive("up", "lg");
  const isShortScreen = useMediaQuery("(max-height: 720px)");

  const userName = profile?.profile?.userName || "";
  const profileHref = useMemo(
    () => `${PROFILE_URL}/Profile/${userName}`,
    [userName]
  );

  // Drawer paper: fixed height, NOT scrollable
  const paperSx = {
    width: NAV_WIDTH,
    bgcolor: "background.default",
    borderRightStyle: "dashed",

    height: "100dvh",
    maxHeight: "100dvh",
    "@supports not (height: 100dvh)": {
      height: "100vh",
      maxHeight: "100vh",
    },

    overflow: "hidden", // ✅ prevents the drawer itself from scrolling
    display: "flex",
    flexDirection: "column",
  };

  const renderContent = (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Logo (fixed) */}
      <Box sx={{ px: 3, pt: 2, pb: 1, display: "inline-flex", flexShrink: 0 }}>
        <Suspense fallback={<CircularProgress size={20} />}>
          <Logo sx={{ height: 30 }} />
        </Suspense>
      </Box>

      {/* Account (fixed) */}
      <Box sx={{ mb: 2, mx: 2.5, flexShrink: 0 }}>
        <a
          href={profileHref}
          style={{ textDecoration: "none" }}
          target="_blank"
          rel="noreferrer"
        >
          <StyledAccount>
            <Box sx={{ position: "relative", display: "flex", flexShrink: 0 }}>
              <Suspense fallback={<CircularProgress size={20} />}>
                <Avatar
                  src={profile?.profile?.profilePicture}
                  alt={`${profile?.firstName || ""} ${profile?.lastName || ""}`}
                  sx={{
                    width: 44,
                    height: 44,
                    border: "2px solid #fff",
                    boxShadow: (theme) =>
                      `0 0 0 1px ${alpha(theme.palette.grey[500], 0.2)}`,
                  }}
                />
              </Suspense>

              <VerifiedSeal>
                <img
                  src="/favicon/contactie_favicon.png"
                  alt="v"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </VerifiedSeal>
            </Box>

            <Box sx={{ ml: 1.5, flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  color: "text.primary",
                  fontWeight: 800,
                  fontSize: "0.88rem",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.2,
                  mb: 0.2,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {profile?.firstName} {profile?.lastName}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  fontSize: "11px",
                  display: "flex",
                  alignItems: "center",
                  opacity: 0.85,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <span style={{ color: "#ff715b", fontWeight: 800, marginRight: 2 }}>
                  @
                </span>
                {userName}
              </Typography>
            </Box>
          </StyledAccount>
        </a>
      </Box>

      {/* ✅ ONLY THIS PART SCROLLS */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0, // CRITICAL for scroll in flex containers
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          px: 1, // keep the scroll area aligned
          pb: 1,

          // nice scrollbar (optional)
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha("#000", 0.18),
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
        }}
      >
        <Suspense
          fallback={
            <Box sx={{ px: 2.5, py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          }
        >
          <NavSection
            firstName={profile?.firstName}
            lastName={profile?.lastName}
            userName={userName}
            category={profile?.category}
            email={profile?.email}
          />
        </Suspense>
      </Box>

      {/* Bottom CTA (fixed) */}
      <Box sx={{ px: 2.5, pb: 2.5, pt: 1.5, flexShrink: 0 }}>
        <Stack
          alignItems="center"
          spacing={1.5}
          sx={{
            pt: isShortScreen ? 3 : 4.5,
            pb: 2,
            borderRadius: 2,
            position: "relative",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
            border: (theme) => `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
            overflow: "visible",
          }}
        >
          <Box
            component="img"
            src="/assets/illustrations/illustration_avatar.png"
            alt="upgrade"
            sx={{
              width: isShortScreen ? 72 : 96,
              position: "absolute",
              top: isShortScreen ? -22 : -32,
              left: "50%",
              transform: "translateX(-50%)",
              pointerEvents: "none",
              userSelect: "none",
            }}
          />

          <Button
            variant="contained"
            size="small"
            onClick={() => navigate("/admin/Bundles")}
            sx={{
              px: 3,
              fontWeight: 800,
              borderRadius: 1.5,
              boxShadow: (theme) => theme.customShadows?.primary,
            }}
          >
            Upgrade plan
          </Button>
        </Stack>
      </Box>
    </Box>
  );

  const nav = (
    <Box component="nav" sx={{ flexShrink: { lg: 0 }, width: { lg: NAV_WIDTH } }}>
      {isDesktop ? (
        <Drawer open variant="permanent" PaperProps={{ sx: paperSx }}>
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: paperSx }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );

  return (
    <>
      {nav}
      <CardPurchaseDialog
        open={showCardDialog}
        onClose={closeCardDialog}
        userEmail={profile?.email}
      />
    </>
  );
}
